<template>
    <span>
        <slot name="activator" :selected="selected" :_id="activatorId">
            <input
                :id="activatorId"
                :value="label"
                readonly
                class="GCB2-audience-setting-base-input select"
            />
        </slot>
        <v-menu
            :close-on-content-click="false"
            :open-on-click="openOnClick"
            v-model="menuOpened"
            :activator="`#${activatorId}`"
            :style="menuWidthStyle"
        >
            <v-card scrollable class="card" :height="cardHeight" :style="menuWidthStyle">
                <v-card-title class="header">
                    <div class="header__content">
                        <v-text-field
                            color="mainNew"
                            :label="label"
                            dense
                            v-model="search"
                            hide-details
                            style="width:100%"
                            append-icon="mdi-magnify"
                            class="header__search-input pt-0"
                            filled
                        >
                        </v-text-field>
                    </div>
                </v-card-title>
                <v-card-text style="overflow:hidden">
                    <div class="select-all-sticky" @click.stop="selectAll">
                        <v-list-item style="cursor: pointer;">
                            <v-icon
                                class="header__checkbox"
                                :color="value.length > 0 ? selectAllIconColor : ''"
                                >{{ selectAllIcon }}
                            </v-icon>
                            <div class="selection-item-wrapper">
                                <div class="selection-item">
                                    <span :style="allLabelBold ? 'font-weight: bold' : ''" class="selection-item-title ml-2">{{ allLabel }}</span>
                                </div>
                            </div>
                        </v-list-item>
                    </div>
                    <div
                        v-if="menuOpened"
                        ref="treeView"
                        class="vue-virtual-scroll-tree"
                        :style="{ height: `${option.height}px` }"
                        @scroll="handleScroll"
                    >
                        <!-- Base -->
                        <div class="vue-virtual-scroll-tree__base" :style="{ height: baseHeight + 'px' }" />

                        <!-- Empty Block -->
                        <div v-if="listData.length === 0" class="vue-virtual-scroll-tree__empty-block">
                            <span class="empty-text">{{ emptyText }}</span>
                        </div>

                        <!-- Tree List -->
                        <div
                            v-else
                            class="vue-virtual-scroll-tree__list"
                            :style="{ transform: `translateY(${offset}px)` }"
                        >
                            <!-- Tree Item -->
                            <template v-for="(item, index) in listData">
                            <div
                                v-if="item._isPinnedSummary"
                                :key="'pinned-summary'"
                                class="pinned-summary"
                                :style="{ height: `${option.itemHeight}px` }"
                                @click.stop="expandPinned"
                            >
                                <div class="pinned-summary__inner">
                                    <span class="pinned-summary__names" :title="pinnedSummaryText">{{ pinnedSummaryText }}</span>
                                    <v-icon class="pinned-summary__icon">mdi-chevron-down</v-icon>
                                </div>
                            </div>
                            <div
                                v-else-if="item._isPinnedToggle"
                                :key="'pinned-toggle'"
                                class="pinned-toggle"
                                :style="{ height: `${option.itemHeight}px` }"
                                @click.stop="collapsePinned"
                            >
                                <span class="pinned-toggle__label">Свернуть</span>
                                <v-icon class="pinned-summary__icon">mdi-chevron-up</v-icon>
                            </div>
                            <div
                                v-else
                                :key="item.id + index"
                                :class="{
                                    ' vue-virtual-scroll-tree__item': true,
                                    disabled: item.disabled,
                                    'pinned-tail': item._isPinnedTail,
                                }"
                                :style="{
                                    height: `${option.itemHeight}px`,
                                }"
                            >
                                <!-- Indent -->
                                <span class="vue-virtual-scroll-tree__indent">
                                    <span
                                        v-for="(indent, key) in item.indents"
                                        :key="key"
                                        :class="{ 'indent-unit': true, line: showLine }"
                                    />
                                </span>

                                <!-- Expand -->

                                <!-- Content -->
                                <div class="vue-virtual-scroll-tree__content" @click="handleClick(item)">
                                    <template>
                                        <template>
                                            <v-simple-checkbox
                                                v-if="!item.children"
                                                class="mr-2"
                                                @click="handleCheckboxClick(item)"
                                                :value="Boolean(valueMap[item.id])"
                                                color="mainNew"
                                                :ripple="false"
                                            ></v-simple-checkbox>
                                            <div v-else>
                                                <v-icon
                                                    class="mr-2"
                                                    @click.stop="handleCheckboxClick(item)"
                                                    :color="getTopLevelCheckboxIcon(item).color"
                                                    >{{ getTopLevelCheckboxIcon(item).icon }}</v-icon
                                                >
                                                <v-icon class="mr-2 c" @click.stop="handleExpandClick(item)">{{
                                                    item.expand ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                                }}</v-icon>
                                            </div>
                                        </template>
                                    </template>

                                    <slot :item="item" :index="index">
                                        <span :title="item.name"> {{ item.name }}</span>
                                    </slot>
                                </div>
                            </div>
                            </template>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-menu>
    </span>
</template>

<script>
import clamp from 'lodash/clamp'
import cloneDeep from 'lodash/cloneDeep'
import difference from 'lodash/difference'
import uniq from 'lodash/uniq'
import concat from 'lodash/concat'
import { v4 as uuidv4 } from 'uuid'

let lastTime = 0

export default {
    name: 'VueVirtualScrollTree',
    components: {},
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: 'Поиск...',
        },
        items: {
            type: Array,
            default: () => [],
        },
        showLine: {
            type: Boolean,
            default: true,
        },
        emptyText: {
            type: String,
            default: 'Нет данных',
        },
        filterNodeMethod: {
            type: Function,
            default: (value, item) => item.name.toLowerCase().includes(value.toLowerCase()),
        },
        defaultExpandAll: {
            type: Boolean,
            default: true,
        },
        timeout: {
            type: Number,
            default: 17,
        },
        transition: { type: Boolean, default: true },
        openOnClick: { type: Boolean, default: true },
        width: { type: Number, default: undefined },
        sort: { type: Boolean, default: true },
        option: {
            type: Object,
            default: () => ({
                height: 500,
                itemHeight: 48,
            }),
        },
        allLabel: {type: String, default: "Все"},
        allLabelBold: {type: Boolean, default: false},
    },
    data() {
        return {
            search: '',
            offset: 0,
            menuOpened: false,
            baseHeight: 0,
            listData: [],
            selectAllIconColor: 'mainNew',
            pinnedIds: [],
            pinnedExpanded: false,
            pinnedGroupExpanded: {},
        }
    },
    computed: {
        activatorId() {
            return 'tree-select-activator' + '-' + uuidv4().slice(0, 10)
        },
        valueMap() {
            const map = {}
            this.value.forEach(id => {
                map[id] = true
            })
            return map
        },
        cardHeight() {
            return clamp(this.baseHeight, 100, this.option.height) + 100
        },
        menuWidthStyle() {
            const w = this.width
            if (w) {
                return `width: ${w}px; min-width: ${w}px; max-width: ${w}px; overflow: hidden;`
            }
            return 'min-width: 200px; overflow: hidden;'
        },
        selectableIds() {
            const ids = []
            ;(this.items || []).forEach(el => {
                ;(el.children || []).forEach(c => {
                    if (!c.disabled) ids.push(c.id)
                })
            })
            return ids
        },
        isAllSelected() {
            return this.selectableIds.length > 0 && this.value.length === this.selectableIds.length
        },
        pinnedLeaves() {
            if (!this.sort || this.isAllSelected) return []
            const idSet = new Set(this.pinnedIds)
            if (!idSet.size) return []
            const leaves = []
            ;(this.items || []).forEach(group => {
                ;(group.children || []).forEach(child => {
                    if (idSet.has(child.id)) {
                        leaves.push({
                            ...child,
                            children: undefined,
                            _groupName: group.name,
                            _isPinnedLeaf: true,
                            indents: [false],
                        })
                    }
                })
            })
            return leaves.sort((a, b) =>
                String(a.name || '').localeCompare(String(b.name || ''), 'ru', { sensitivity: 'base' })
            )
        },
        canCollapsePinned() {
            return this.pinnedLeaves.length > 3 && !this.isAllSelected
        },
        visiblePinnedLeaves() {
            return this.pinnedLeaves.filter(leaf => this.itemMatchesSearch(leaf, leaf._groupName))
        },
        pinnedListRows() {
            if (!this.pinnedLeaves.length) return []
            if (this.canCollapsePinned && !this.pinnedExpanded) {
                return [{ id: '__pinned-summary', _isPinnedSummary: true }]
            }
            const groups = new Map()
            const ungrouped = []
            this.visiblePinnedLeaves.forEach(leaf => {
                const name = leaf._groupName
                if (name) {
                    if (!groups.has(name)) groups.set(name, [])
                    groups.get(name).push(leaf)
                } else {
                    ungrouped.push(leaf)
                }
            })
            const rows = []
            const names = [...groups.keys()].sort((a, b) =>
                a.localeCompare(b, 'ru', { sensitivity: 'base' })
            )
            names.forEach(name => {
                rows.push(...this.buildPinnedGroupRows(name, name, groups.get(name)))
            })
            if (ungrouped.length) {
                rows.push(...this.buildPinnedGroupRows('__ungrouped', 'Прочее', ungrouped))
            }
            if (this.canCollapsePinned) {
                rows.push({ id: '__pinned-toggle', _isPinnedToggle: true })
            } else if (rows.length) {
                rows[rows.length - 1] = { ...rows[rows.length - 1], _isPinnedTail: true }
            }
            return rows
        },
        pinnedSummaryText() {
            const selected = Array.isArray(this.value) ? this.value : []
            const query = String(this.search || '').trim()
            if (query) {
                const selectedSet = new Set(selected)
                let matched = 0
                ;(this.items || []).forEach(group => {
                    ;(group.children || []).forEach(child => {
                        if (selectedSet.has(child.id) && this.itemMatchesSearch(child, group.name)) {
                            matched++
                        }
                    })
                })
                return `Выбрано: ${matched}(${selected.length})`
            }
            const pinnedSet = new Set(this.pinnedIds)
            const n = selected.filter(id => pinnedSet.has(id)).length
            const m = selected.filter(id => !pinnedSet.has(id)).length
            return m > 0 ? `Выбрано: ${n}(+${m})` : `Выбрано: ${n}`
        },
        flattenTree() {
            const flatten = (treeData, level = 1, parent = null) =>
                treeData.reduce((acc, v) => {
                    if (!v.id) {
                        v.id = uuidv4()
                    }

                    v.level = level
                    v.parent = parent

                    v.indents = []
                    for (let i = 0; i < v.level - 1; i++) {
                        v.indents[i] = false
                    }

                    if (v.disabled === undefined) {
                        v.disabled = false
                    }

                    if (v.locked === undefined) {
                        v.locked = false
                    }

                    if (v.expand === undefined) {
                        v.expand = this.defaultExpandAll
                    }

                    if (v.visible === undefined) {
                        v.visible = true
                    }

                    if (!parent.visible || !parent.expand) {
                        v.visible = false
                    }

                    acc.push(v)
                    if (v.children) {
                        acc.push(...flatten(v.children, level + 1, v))
                    }

                    return acc
                }, [])
            const clone = cloneDeep(this.items)
            if (this.sort) {
                const query = (this.search || '').trim().toLowerCase()
                const compare = (a, b) => {
                    if (query) {
                        const rankA = this.getSearchRank(String(a.name || ''), query)
                        const rankB = this.getSearchRank(String(b.name || ''), query)
                        if (rankA !== rankB) return rankA - rankB
                    }
                    return String(a.name || '').localeCompare(String(b.name || ''), 'ru', { sensitivity: 'base' })
                }
                clone.sort(compare)
                clone.forEach(group => {
                    if (group.children) group.children.sort(compare)
                })
            }
            return flatten(clone, 1, {
                id: uuidv4(),
                level: 0,
                visible: true,
                expand: true,
                children: clone,
            })
        },

        visibleListCount() {
            return Math.floor(this.option.height / this.option.itemHeight)
        },
        selected() {
            return this.flattenTree.filter(el => this.valueMap[el.id])
        },
        selectedAll() {
            return this.isAllSelected
        },
        selectAllIcon() {
            if (this.selectedAll) return 'mdi-checkbox-marked'
            if (this.value.length) return 'mdi-minus-box'
            return 'mdi-checkbox-blank-outline'
        }
    },
    watch: {
        items(v) {
            this.updateTreeView()
        },
        menuOpened(v) {
            if (v) {
                if (this.sort) {
                    const ids = [...(this.value || [])]
                    const allSelected = ids.length && ids.length === this.selectableIds.length
                    this.pinnedIds = allSelected ? [] : ids
                    this.pinnedExpanded = false
                    this.pinnedGroupExpanded = {}
                }
                setTimeout(() => this.updateTreeView(), 10)
                this.search = ''
            }
        },
        search(v) {
            if (this.$refs.treeView) {
                this.$refs.treeView.scrollTop = 0
            }
            this.filter(v)
        },
        selected(v) {
            this.$emit('update-selected', this.selected)
        },
    },
    methods: {
        itemMatchesSearch(item, groupName) {
            const raw = this.search || ''
            if (!String(raw).trim()) return true
            if (item && this.filterNodeMethod(raw, item)) return true
            if (groupName && String(groupName).toLowerCase().includes(String(raw).trim().toLowerCase())) {
                return true
            }
            return false
        },
        getSearchRank(label, query) {
            const name = label.toLowerCase()
            if (name.startsWith(query)) return 0
            const index = name.indexOf(query)
            if (index === -1) return 3
            if (index + query.length === name.length) return 2
            return 1
        },
        expandPinned() {
            this.pinnedExpanded = true
            this.updateTreeView()
        },
        collapsePinned() {
            this.pinnedExpanded = false
            if (this.$refs.treeView) {
                this.$refs.treeView.scrollTop = 0
            }
            this.updateTreeView()
        },
        buildPinnedGroupRows(key, name, children) {
            const expanded = this.pinnedGroupExpanded[key] !== false
            const rows = [
                {
                    id: '__pinned-group-' + key,
                    name,
                    children,
                    expand: expanded,
                    indents: [],
                    _isPinnedGroup: true,
                    _pinnedKey: key,
                },
            ]
            if (expanded) rows.push(...children)
            return rows
        },
        handleExpandClick(item) {
            if (item._isPinnedGroup) {
                const key = item._pinnedKey
                const expanded = this.pinnedGroupExpanded[key] !== false
                this.pinnedGroupExpanded = {
                    ...this.pinnedGroupExpanded,
                    [key]: !expanded,
                }
                this.updateTreeView()
                return
            }
            if (item.children) {
                this.toggleExpand(item)
            }
        },
        unpin(ids) {
            if (!this.sort) return
            const remove = new Set(Array.isArray(ids) ? ids : [ids])
            this.pinnedIds = this.pinnedIds.filter(id => !remove.has(id))
            this.updateTreeView()
        },
        getPinnedSet() {
            if (this.sort && !this.isAllSelected && this.pinnedIds.length) {
                return new Set(this.pinnedIds)
            }
            return null
        },
        getGroupActionChildren(item) {
            const children = item.children || []
            if (item._isPinnedGroup) {
                return children.filter(c => !c.disabled)
            }
            const pinnedSet = this.getPinnedSet()
            return children.filter(c => {
                if (c.disabled) return false
                if (pinnedSet && pinnedSet.has(c.id)) return false
                return true
            })
        },
        getVisibleTreeData() {
            const pinnedSet = this.getPinnedSet()
            return this.flattenTree.filter(v => {
                if (!v.visible) return false
                if (pinnedSet && !v.children && pinnedSet.has(v.id)) return false
                if (pinnedSet && v.children && v.children.length) {
                    const allChildrenPinned = v.children.every(c => pinnedSet.has(c.id))
                    if (allChildrenPinned) return false
                }
                return true
            })
        },
        handleClick(item) {
            if (item._isPinnedSummary || item._isPinnedToggle) return
            if (item._isPinnedGroup) {
                this.handleExpandClick(item)
                return
            }
            this.toogleCheckbox(item)
            if (item.children) {
                this.toggleExpand(item)
            }

            if (!item.disabled) {
                this.$emit('node-click', item)
            }
        },

        filter(value) {
            console.log('m', this.filterNodeMethod)
            if (!this.filterNodeMethod)
                throw new Error('[VueVirtualScrollTree] filterNodeMethod is required when filter')

            const filterVisible = item => {
                item.visible = true
                item.expand = true
                item.locked = false
                if (item.parent) {
                    filterVisible(item.parent)
                }
            }

            this.flattenTree.forEach(item => {
                if (this.filterNodeMethod(value, item)) {
                    filterVisible(item)
                } else {
                    item.visible = false
                    item.locked = true
                }
            })

            this.updateTreeView()
        },

        updateTreeView() {
            const visibleCount = this.getVisibleTreeData().length + this.pinnedListRows.length
            this.baseHeight = visibleCount * this.option.itemHeight
            const el = this.$refs.treeView
            const viewHeight = (el && el.clientHeight) || this.option.height
            let scrollTop = el ? el.scrollTop : 0
            const maxScroll = Math.max(0, this.baseHeight - viewHeight)
            if (scrollTop > maxScroll) {
                scrollTop = maxScroll
                if (el) el.scrollTop = scrollTop
            }
            this.updateList(scrollTop)
        },

        handleScroll() {
            const currentTime = +new Date()
            if (currentTime - lastTime > this.timeout) {
                if (this.$refs.treeView) {
                    this.updateList(this.$refs.treeView.scrollTop)
                    this.updateList(this.$refs.treeView.scrollTop)
                    lastTime = currentTime
                }
            }
        },

        updateList(scrollTop = 0) {
            let start = Math.floor(scrollTop / this.option.itemHeight) - Math.floor(this.visibleListCount / 2)
            start = start < 0 ? 0 : start
            const end = start + this.visibleListCount * 2

            const treeData = this.getVisibleTreeData()
            const visibleData = [...this.pinnedListRows, ...treeData]

            if (this.showLine) {
                const siblingMap = new Map()
                const indexMap = new Map()
                treeData.forEach(v => {
                    if (!v.parent) return
                    const siblingCount = siblingMap.get(v.parent.id)
                    if (siblingCount === undefined) {
                        siblingMap.set(v.parent.id, 1)
                    } else {
                        siblingMap.set(v.parent.id, siblingCount + 1)
                    }

                    indexMap.set(v.parent.id, 0)
                })

                const indentMap = new Map()
                treeData.forEach(v => {
                    if (!v.parent) return
                    const siblingCount = siblingMap.get(v.parent.id)
                    const selfIndex = indexMap.get(v.parent.id)
                    if (selfIndex === siblingCount - 1) {
                        v.last = true
                        indentMap.set(v.level - 1, false)
                    } else {
                        v.last = false
                        indentMap.set(v.level - 1, true)
                    }

                    indexMap.set(v.parent.id, selfIndex + 1)

                    for (let i = 0; i < v.level - 1; i++) {
                        v.indents[i] = indentMap.get(i)
                    }
                })
            }

            this.listData = visibleData.slice(start, end)

            this.offset = start * this.option.itemHeight
        },

        toggleExpand(item) {
            if (item.expand) {
                this.collapse(item, true)
            } else {
                this.expand(item, true)
            }

            this.updateTreeView()
        },

        expand(item) {
            item.expand = true
            this.updateExpand(item.children, true)
        },

        collapse(item) {
            item.expand = false
            this.updateExpand(item.children, false)
        },

        updateExpand(children, visible) {
            children &&
                children.forEach(node => {
                    if (!node.locked) {
                        node.visible = visible
                        if (node.children && node.expand) {
                            this.updateExpand(node.children, visible)
                        }
                    }
                })
        },

        expandAll() {
            this.flattenTree.forEach(item => {
                if (!item.locked) {
                    item.expand = true
                    item.visible = true
                }
            })

            this.updateTreeView()
        },

        collapseAll(level = 1) {
            this.flattenTree.forEach(item => {
                item.expand = false
                if (item.level !== level) {
                    item.visible = false
                }
            })

            this.updateTreeView()
        },
        handleCheckboxClick(item) {
            if (item._isPinnedSummary || item._isPinnedToggle) return
            if (item.children) {
                const children = this.getGroupActionChildren(item)
                if (!children.length) return
                const ids = children.map(el => el.id)
                if (children.every(c => this.valueMap[c.id])) {
                    this.unpin(ids)
                    this.update(difference(this.value, ids))
                } else {
                    this.update(uniq(concat(this.value, ids)))
                }
                this.updateTreeView()
            } else {
                this.toogleCheckbox(item)
            }
        },
        getTopLevelCheckboxIcon(item) {
            const children = this.getGroupActionChildren(item)
            if (!children.length) {
                return { icon: 'mdi-checkbox-blank-outline', color: undefined }
            }
            if (children.every(c => this.valueMap[c.id])) {
                return { icon: 'mdi-checkbox-outline', color: 'mainNew' }
            }
            if (children.every(c => !this.valueMap[c.id])) {
                return { icon: 'mdi-checkbox-blank-outline', color: undefined }
            }
            return { icon: 'mdi-minus-box-outline', color: 'mainNew' }
        },
        toogleCheckbox(item) {
            if (item.children) {
            } else {
                if (this.valueMap[item.id]) {
                    this.unpin(item.id)
                    this.update(this.value.filter(id => id !== item.id))
                } else {
                    this.update(this.value.concat(item.id))
                }
                this.updateTreeView()
            }
        },
        update(val) {
            this.$emit('input', val)
        },
        selectAll() {
            if (this.value.length) {
                this.pinnedIds = []
                this.update([])
            } else {
                const res = []
                const filteredArr = this.items?.filter(el => !el.disabled)
                filteredArr.forEach(el => {
                    const itemRes = el.children.filter(i => !i.disabled).map(i => i.id)
                    res.push(...itemRes)
                })
                this.update(res)
            }
            this.updateTreeView()
        }
    },
}
</script>

<style scoped lang="sass">
@import @/vars.sass
.vue-virtual-scroll-tree
    position: relative
    overflow: auto
    &__base
        position: absolute
        left: 0
        top: 0
        right: 0
        z-index: -1
    &__empty-block
        position: relative
        min-height: 100px
        text-align: center
        width: 100%
        .empty-text
            position: absolute
            left: 50%
            top: 50%
            -webkit-transform: translate(-50%, -50%)
            transform: translate(-50%, -50%)
            color: #909399
            font-size: 14px
    &__list
        position: absolute
        left: 0
        right: 0
        top: 0
        min-height: 100px
        padding-left: 12px
        box-sizing: border-box
    &__item
        display: flex
        align-items: flex-start
        outline: none
        &:hover
            background-color: $light-gray-5
        &:focus
            background-color: $light-gray-5
        &.disabled
            color: rgba(0, 0, 0, 0.25)
            cursor: not-allowed
        .vue-virtual-scroll-tree__content
            width: 100%
            display: flex
            align-items: center
            white-space: nowrap
            overflow: hidden
            text-overflow: ellipsis
    &__content
        display: flex
        align-items: center
        width: 100%
        position: relative
        z-index: auto
        height: 100%
        margin: 0
        padding: 0 4px
        line-height: 24px
        background: transparent
        border-radius: 2px
        cursor: pointer
        user-select: none
    &__expand
        padding: 6px
        cursor: pointer
        color: #c0c4cc
        font-size: 12px
        transform: rotate(0deg)
        /* transition: transform .3s ease-in-out; */
        &.expanded
            transform: rotate(90deg)
    &__icon
        margin-right: 8px
    &__indent
        align-self: stretch
        white-space: nowrap
        user-select: none

        .indent-unit
            display: inline-block
            width: 24px
            position: relative
            height: 100%
            &.line
                &::before
                    position: absolute
                    top: 0
                    right: 11px
                    bottom: 0
                    border-right: 1px solid #d9d9d9
                    content: ""







.header
    border-bottom: 1px solid $light-gray-4
    &__content
        display: flex
        align-items: center
        width: 100%
    &__search-input
        background-color: $light-gray-5
        flex-grow: 1
.card
    min-width: 200px
    overflow: hidden
    box-sizing: border-box
.v-card__title
    padding-top:0px
    padding-bottom:0px
    padding-left:0px
    padding-right:0px
.selection-item-wrapper
    width: 100%
    height:48px
    display: flex
    align-items: center
    overflow: hidden
    text-overflow: ellipsis
.selection-item
    width: 100%
    display: flex
    justify-content: space-between
    align-items: center
    white-space: nowrap
    align-self
    overflow: hidden
    text-overflow: ellipsis
.selection-item-title.disabled
    opacity: 0.7
::v-deep .v-simple-checkbox--disabled
    opacity: 0.7
.v-card__text
    padding:0
    position: relative
    overflow: auto !important
    overflow-x: hidden !important
.select-all-sticky
    position: sticky
    top: 0
    z-index: 2
    background: #fff
    border-bottom: 1px solid $light-gray-4
    .v-list-item
        transition: background-color 0.15s ease
    .v-list-item:hover
        background-color: $light-gray-5
.pinned-summary,
.pinned-toggle
    margin-left: -12px
    width: calc(100% + 12px)
    min-height: 48px
    height: 48px
    background: $light-gray-5
    border-bottom: 1px solid $light-gray-4
    cursor: pointer
    overflow: hidden
    box-sizing: border-box
    display: flex
    align-items: center
    &:hover
        background-color: $light-gray-4
.pinned-summary
    position: relative
.pinned-summary__inner
    position: absolute
    left: 16px
    right: 16px
    top: 0
    bottom: 0
    display: flex
    align-items: center
    min-width: 0
    overflow: hidden
.pinned-summary__names
    display: block
    min-width: 0
    flex: 1
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
.pinned-summary__icon
    color: $gray-2
    flex-shrink: 0
    margin-left: 8px
.pinned-toggle
    justify-content: space-between
    padding: 0 16px
.pinned-toggle__label
    font-size: 13px
    color: $gray
.picker-group
    padding: 8px 12px 4px
    color: $gray-3
    font-size: 10px
    font-weight: 800
    letter-spacing: .08em
    text-transform: uppercase
    line-height: 14px
    display: flex
    align-items: center
    box-sizing: border-box
.pinned-tail
    position: relative
    border-bottom: none !important
    box-shadow: none
    &:after
        content: ''
        position: absolute
        left: -12px
        right: 0
        bottom: 0
        height: 1px
        background: $light-gray-2
::v-deep .v-text-field__slot input
    color: $text-color
    font-size: 14px
::v-deep label
    font-size: 14px
    color: $gray !important
.highlighted
    background-color: $main-light
.v-list
    padding: 0
.disabled
    color: $gray-2
    pointer-events: none
    cursor: default
::v-deep .v-input__slot:before
    border-color: $light-gray-4 !important
::v-deep .v-input--selection-controls__input
    margin-right: 0 !important
*
    scrollbar-width: auto
    scrollbar-color: $light-gray #ffffff

*::-webkit-scrollbar
    width: 14px
*::-webkit-scrollbar-track
    background: #ffffff

*::-webkit-scrollbar-thumb
    background-color: $gray-2
    border-radius: 8px
    border: 4px solid #ffffff
*::-webkit-scrollbar-thumb:hover
    background-color: $gray
</style>
